import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { bucketTopN } from "./bucketTopN";
import ChartState from "./ChartState";
import { pluralize } from "./kpiHelpers";

// Right-hand "Ranked breakdown" panel from the approved prototype — the same
// bucketed distribution the chart card shows, as a plain ranked list
// (swatch, name, count, %). Recomputes bucketTopN off the raw slice using the
// same config fields the chart card uses, rather than reading the chart
// card's internal state, so it stays a simple, decoupled sibling.
const ReportRankedBreakdown = ({ data, loading, config, hideNoise = true }) => {
  const { t } = useTranslation();

  const { rows, colors } = useMemo(() => {
    const list = config.noiseFilter && hideNoise
      ? (data || []).filter((item) => config.noiseFilter.isValid(item[config.labelField]))
      : data || [];
    const { chartData, colors: bucketColors } = bucketTopN(list, {
      labelKey: config.labelField,
      valueKey: config.valueField,
      chartLabel: config.dimensionLabel,
      othersLabel: t("Others"),
    });
    return { rows: chartData.slice(1), colors: bucketColors };
  }, [data, config, hideNoise, t]);

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <div>
          <h3>{t("Ranked Breakdown")}</h3>
          <div className="edr-panel-desc">
            {t("Top {{dim}}", { dim: pluralize(t(config.dimensionLabel)?.toLowerCase()) })}
          </div>
        </div>
      </div>
      {!loading && rows.length > 0 ? (
        <ul className="edr-rank">
          {rows.map((row, index) => {
            const label = row[0].replace(/\s*\([^)]*\)\s*/g, "");
            const pctMatch = row[0].match(/\(([^)]+)\)/);
            const pct = pctMatch ? pctMatch[1] : "";
            return (
              <li key={index}>
                <span className="edr-rank-sw" style={{ backgroundColor: colors[index] }}></span>
                <span className="edr-rank-nm">{label}</span>
                {!config.percentOnly && <span className="edr-rank-ct">{row[1].toLocaleString()}</span>}
                <span className="edr-rank-pc">{pct}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <ChartState loading={loading} />
      )}
    </div>
  );
};

export default ReportRankedBreakdown;
